import { Loading } from '@components/atomic/atoms';
import { getProject } from '@lib/api_ts/projectApi';
import { useQuery } from '@tanstack/react-query';
import { getCoordinatorByProjectId } from 'lib/api_ts/coordinatorApi';
import { getPartnersByCompanyId } from 'lib/api_ts/parternshipApi';
import { createContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

export type PermissionType = {
	name: string;
	labelIt: string;
	labelEn?: string;
	descriptionIt?: string;
	descriptionEn?: string;
	category?: string;
};

// Tipi per l'utente e per il contesto
type UserType = {
	id: string;
	firstName: string;
	lastName: string;
	email: string;
	isOe?: boolean;
	roles?: number[];
	companies?: number[]; // Aggiunto per rappresentare le aziende associate all'utente
	permissions?: PermissionType[];
};

type UserAccessType = {
	isSuperAdmin: boolean;
	isAdmin: boolean;
	isOeExternal: boolean;
	isPartnerExtended: boolean;
	isAbovePartner: boolean;
	isPartner: boolean;
	isAboveGuest: boolean;
	isGuest: boolean;
	isAboveAuditor: boolean;
	isAuditor: boolean;
	isGuestEnteErogante: boolean;
};

type UserContextType = {
	myUser: UserType | null;
	isCoordinator: boolean | undefined;
	isBeneficiary: boolean | undefined;
	isPartnerUser: boolean | undefined;
	isOe: boolean | undefined;
	userAccess: UserAccessType;
	loginContext: (userData: UserType) => void;
	logoutContext: () => void;
	loadingContext: boolean;
	setMyUser: React.Dispatch<React.SetStateAction<UserType | null>>;
	updateUser: (userData: UserType) => void;
};

export const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
	const location = useLocation();
	const projectId = location.pathname.split('/').at(2);
	const isNumeric = !isNaN(Number(projectId));
	const [myUser, setMyUser] = useState<UserType>(null);
	const [loadingContext, setLoadingContext] = useState(true);
	const [isBeneficiary, setIsBeneficiary] = useState<boolean>();
	const [isCoordinator, setIsCoordinator] = useState<boolean>();

	const isOe = useMemo(() => myUser?.isOe, [myUser]);

	const userAccess = useMemo(() => {
		const role = myUser?.roles?.length ? Math.min(...myUser.roles) : 70;

		return {
			isSuperAdmin: role === 1,
			isAdmin: role <= 2,
			isExternalAdmin: role <= 4,
			isOeExternal: role <= 30,
			isAboveIncludingProjectManager: role <= 31,
			isProjectManager: role === 31,
			isPartnerExtended: role <= 39,
			isAbovePartner: role < 40,
			isPartner: role <= 40,
			isAboveGuest: role < 50,
			isGuest: role <= 50,
			isAboveAuditor: role < 70,
			isAuditor: role <= 70,
			isGuestEnteErogante: role === 71,
		};
	}, [myUser]);

	const { data: projectInfo, isPending: isProjectInfoPending } = useQuery({
		queryKey: ['project', projectId],
		queryFn: async () => getProject(projectId),
		enabled: projectId !== undefined && !!projectId && isNumeric,
	});

	const { data: partnersFromApi, isPending: isPartnersPending } = useQuery({
		queryKey: ['partners', projectInfo?.companyId],
		initialData: [],
		queryFn: async () => getPartnersByCompanyId(`${projectInfo.companyId}`),
		enabled: !!projectInfo?.companyId,
	});

	useEffect(() => {
		if (!projectId || !isNumeric || isProjectInfoPending || isPartnersPending) {
			setIsCoordinator(undefined);
			setIsBeneficiary(undefined);
			return;
		}

		(async () => {
			try {
				const coordinatorResp = await getCoordinatorByProjectId(projectId);
				setIsCoordinator(coordinatorResp.isCoordinator);
				setIsBeneficiary(coordinatorResp.hasBeneficiaryRole);
			} catch (error) {
				console.error('Error fetching coordinator info:', error);
				setIsCoordinator(undefined);
				setIsBeneficiary(undefined);
			}
		})();
	}, [projectId, partnersFromApi, projectInfo?.companyId, isProjectInfoPending, isPartnersPending, isNumeric]);

	//Recupera i dati dell'utente da localStorage al primo render
	useEffect(() => {
		const storedUser = localStorage.getItem('myUser');

		if (storedUser) {
			try {
				const parsedUser = JSON.parse(storedUser);
				setMyUser(parsedUser);
			} catch (error) {
				console.error('Errore nel parsing di localStorage:', error);
			}
		}
		setLoadingContext(false);
	}, []);

	const updateUser = (userData: UserType) => {
		localStorage.setItem('myUser', JSON.stringify(userData));
		setMyUser(userData);
	};

	const loginContext = (userData: UserType) => {
		updateUser(userData);
	};

	const logoutContext = () => {
		setMyUser(null);
		localStorage.removeItem('myUser');
	};

	return (
		<UserContext.Provider
			value={{
				myUser,
				isOe,
				isCoordinator,
				isBeneficiary,
				isPartnerUser: isCoordinator || isBeneficiary,
				userAccess,
				loginContext,
				logoutContext,
				loadingContext,
				setMyUser,
				updateUser,
			}}
		>
			{loadingContext ? <Loading fixedHeight="100dvh" /> : children}
		</UserContext.Provider>
	);
};
